/*
 * ============================================================
 * [MK vendor-mod] Murnauer Kaffeerösterei custom patches
 * Patches in dieser Datei (suche: "[MK vendor-mod]"):
 *   1. Zielpunkt gegen weggescrollten Header absichern (Reveal + Clamp)
 *   2. Animation erst starten, wenn Start-/Zielpunkt feststehen
 * Doku: .claude/memory/project_overview.md → "Add-to-Cart-Flug"
 * ============================================================
 */
import { clamp, yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/* [MK vendor-mod] Notbremse, falls die Header-Transition nie endet, + Rand für den Clamp. */
const REVEAL_TIMEOUT = 400;
const VIEWPORT_MARGIN = 8;

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    // [MK vendor-mod] Die CSS-Animation läuft ab dem Einhängen ins DOM, die Punkte stehen erst
    // später fest — solange anhalten, sonst blitzt das Bild an Position 0/0 auf.
    this.style.animationPlayState = 'paused';
    this.#ensureDestinationVisible().then(() => this.#observe());
  }

  /* [MK vendor-mod] BEGIN — Ziel absichern, siehe Doku oben */

  /**
   * Der Zielpunkt wird EINMAL beim Klick gemessen — ist die Header-Leiste weggescrollt, flöge
   * das Bild aus dem Viewport. Sie vorher über ihre eigenen Zustands-Attribute hereinholen;
   * header.js setzt sie beim nächsten Scroll selbst zurück.
   */
  #ensureDestinationVisible = async () => {
    if (this.#destinationVisible()) return;

    const header = document.querySelector('header-component[sticky]');
    if (!(header instanceof HTMLElement)) return;
    const section = header.closest('.header-section') ?? header;

    // Schritt 1: die mobil unten fixierte Leiste hereinholen.
    header.dataset.scrollDirection = 'up';
    await this.#settle(section);
    if (this.#destinationVisible()) return;

    // Schritt 2: nur falls nötig (Desktop). Reihenfolge ist Pflicht — die sticky-Regel ist
    // spezifischer als die mobile Bottom-Bar-Regel und risse die Leiste sonst mobil nach oben.
    header.dataset.stickyState = 'active';
    await this.#settle(section);
  };

  /** Ohne Ziel-Element: true, dann gibt es nichts hereinzuholen. */
  #destinationVisible = () => {
    const rect = this.destination?.getBoundingClientRect();
    return !rect || (rect.top >= 0 && rect.bottom <= window.innerHeight);
  };

  /**
   * Einen Frame warten, damit die Transition startet, dann auf ihr Ende.
   * @param {Element} element
   */
  #settle = async (element) => {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const transitions = element.getAnimations?.() ?? [];
    await Promise.race([
      Promise.allSettled(transitions.map((transition) => transition.finished)),
      new Promise((resolve) => setTimeout(resolve, REVEAL_TIMEOUT)),
    ]);
  };

  /* [MK vendor-mod] END — Ziel absichern */

  #observe() {
    if (!this.isConnected) return;

    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    // [MK vendor-mod] Netz, falls der Warenkorb trotz Reveal draußen liegt (Header ohne sticky).
    const endPoint = {
      x: clamp(destinationRect.left + destinationRect.width / 2, VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN),
      y: clamp(destinationRect.top + destinationRect.height / 2, VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN),
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    // [MK vendor-mod] Punkte stehen fest — Animation freigeben (siehe connectedCallback).
    this.style.animationPlayState = '';

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
